
import { Component, Vue, Prop, Watch } from "vue-property-decorator"
import vSelect from "vue-select"
import api from "@/services/api"
import { FilePathMappingType, SyncObjectType, Integration } from "@/types/integration"
import DeleteButton from "@/components/New/Page/PageComponents/DeleteButton.vue"
import FieldMapping from "./FieldMapping.vue"
import { FieldTypeId } from "@/types/fieldTypes"

@Component({
	components: {
		"v-select": vSelect,
		"delete-btn": DeleteButton,
		"field-mapping": FieldMapping
	}
})
export default class FilePathMappingRow extends Vue {
	/* --- Props --- */

	@Prop({ required: true })
	mapping!: FilePathMappingType

	@Prop({ type: Number, required: true })
	index!: number

	@Prop({ type: Array, default: () => [] })
	syncObjects!: any[]

	@Prop({ type: Array, default: () => [] })
	usedSyncObjectIds!: string[]

	@Prop({ type: Array, default: () => [] })
	fields!: any[]

	@Prop({ required: true })
	syncObjectType!: SyncObjectType

	@Prop({ required: true })
	integration!: Integration

	/* --- Data --- */

	syncObjectTypeEnum = SyncObjectType
	isModalVisible: boolean = false
	localFieldMapping: Record<string, string> = { ...this.mapping.fieldMapping }
	localPrimaryColumn: string | null = this.mapping.primaryColumn

	casesFields: any[] = []

	/* --- Watchers --- */

	@Watch("mapping.fieldMapping", { immediate: true, deep: true })
	private onMappingFieldMappingChanged (newVal: Record<string, string>) {
		this.localFieldMapping = { ...newVal }
	}

	@Watch("mapping.primaryColumn", { immediate: true })
	private onPrimaryColumnChanged (newVal: string | null) {
		this.localPrimaryColumn = newVal
	}

	/* --- Computed Properties --- */

	private get fieldMappingLength (): number {
		return Object.keys(this.localFieldMapping || {}).length
	}

	private get allowedDataTypes (): string[] {
		if (this.syncObjectType === this.syncObjectTypeEnum.DataSource) {
			return [
				FieldTypeId.Text,
				FieldTypeId.TextArea,
				FieldTypeId.Number,
				FieldTypeId.Dollar,
				FieldTypeId.List,
				FieldTypeId.ListMultiple,
				FieldTypeId.ExternalLink,
				FieldTypeId.Date,
				FieldTypeId.DateTime,
				FieldTypeId.Checkbox
			]
		}
		return []
	}

	private get availableSyncObjects (): any[] {
		if (this.mapping.id) {
			// Filter out used sync objects except the current one
			return this.syncObjects.filter(obj =>
				obj.id === this.mapping.id || !this.usedSyncObjectIds.includes(obj.id)
			)
		}

		// For new/empty mappings, filter out all used IDs
		return this.syncObjects.filter(obj => !this.usedSyncObjectIds.includes(obj.id))
	}

	/* --- Methods --- */

	private async mounted () {
		if (this.syncObjectType === this.syncObjectTypeEnum.DataSource && this.mapping.id) {
			try {
				const definitionDetails = await api.definitionDetailsForSource(this.mapping.id)
				this.casesFields = (definitionDetails.fields?.map(fieldInstance => fieldInstance.field) || [])
					.filter(field => field.dataType && this.allowedDataTypes.includes(field.dataType))
			} catch (error) {
				console.error("Failed to fetch definition details for data source:", error)
				this.casesFields = []
			}
		}
	}

	private openModal () {
		this.isModalVisible = true
	}

	private closeModal () {
		this.$emit("updateFieldMapping", this.index, this.localFieldMapping)
		this.$emit("updateMapping", this.index, "primaryColumn", this.localPrimaryColumn)
		this.isModalVisible = false
	}

	private cancel () {
		this.localFieldMapping = { ...this.mapping.fieldMapping }
		this.localPrimaryColumn = this.mapping.primaryColumn
		this.isModalVisible = false
	}

	private async handleUpdate (field: "id" | "primaryColumn" | "filePath", value: string) {
		if (field === "id" && this.syncObjectType === this.syncObjectTypeEnum.DataSource) {
			if (!value) {
				this.$emit("updateFieldMapping", this.index, null)
				this.$emit("updateMapping", this.index, "primaryColumn", null)
				this.casesFields = []
			} else {
				await this.handleDataSourceSelection(value)
				this.$emit("updateFieldMapping", this.index, this.localFieldMapping)
				this.$emit("updateMapping", this.index, "primaryColumn", null)
			}
		}

		this.$emit("updateMapping", this.index, field, value)
	}

	private handleRemove () {
		this.$emit("removeMapping", this.index)
	}

	private async handleDataSourceSelection (dataSourceId: string) {
		if (dataSourceId === null || dataSourceId === undefined) {
			this.casesFields = []
			return
		}

		try {
			const definitionDetails = await api.definitionDetailsForSource(dataSourceId)
			this.casesFields = (definitionDetails.fields?.map(fieldInstance => fieldInstance.field) || [])
				.filter(field => field.dataType && this.allowedDataTypes.includes(field.dataType))

			this.localFieldMapping = this.casesFields.reduce((mapping, field) => {
				if (field.title && field.id) {
					mapping[field.title] = field.id
				}
				return mapping
			}, {} as Record<string, string>)
		} catch (error) {
			console.error("Failed to fetch definition details for data source:", error)
			this.casesFields = []
		}
	}

	private getSyncObjectTypeLabel (type: SyncObjectType): string {
		switch (type) {
			case SyncObjectType.DataSource:
				return "data source"
			default:
				return ""
		}
	}
}
